import {
  ERROR_CODES,
  type ImportRun,
  type ImportRunStatus,
  type ImportSource,
  isPanelCounts,
  PURCHASE_HISTORY_IMPORT_MAX_BYTES,
  VENDOR_PANEL_IMPORT_MAX_BYTES,
} from "@procuremate/shared";
import { clsx } from "clsx";
import type { TFunction } from "i18next";
import { CircleAlert, CircleCheck, Clock, LoaderCircle, Upload } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { TextField } from "@/components/ui/text-field";
import { ApiRequestError } from "@/lib/api-client";
import { Card } from "./admin-layout";
import { useImportRuns, useUploadImport } from "./api";

/**
 * Admin → Imports (WP-3.3, ADR-0026): upload the purchase-history export; the worker applies it
 * and the list follows each run until it has finished.
 */
export function ImportsPage() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-6">
      <Card title={t("admin.imports.title")} headingLevel={1}>
        <p className="mb-4 max-w-prose text-sm text-fg-muted">{t("admin.imports.intro")}</p>
        <UploadForm source="purchase_history" />
      </Card>
      <Card title={t("admin.imports.panel.title")}>
        <p className="mb-4 max-w-prose text-sm text-fg-muted">{t("admin.imports.panel.intro")}</p>
        <UploadForm source="vendor_panel" />
      </Card>
      <RecentRuns />
    </div>
  );
}

const MAX_BYTES: Record<ImportSource, number> = {
  purchase_history: PURCHASE_HISTORY_IMPORT_MAX_BYTES,
  vendor_panel: VENDOR_PANEL_IMPORT_MAX_BYTES,
};

function UploadForm({ source }: { source: ImportSource }) {
  const { t } = useTranslation();
  const mutation = useUploadImport(source);
  const panel = source === "vendor_panel";
  const [file, setFile] = useState<File | null>(null);
  const [inputKey, setInputKey] = useState(0);
  const [localError, setLocalError] = useState<string | undefined>();

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!file) {
      setLocalError(t("admin.imports.chooseFile"));
      return;
    }
    if (file.size > MAX_BYTES[source]) {
      setLocalError(t(panel ? "admin.imports.panel.tooLarge" : "admin.imports.refused.too_large"));
      return;
    }
    setLocalError(undefined);
    mutation.mutate(file, {
      onSuccess: () => {
        setFile(null);
        setInputKey((k) => k + 1);
      },
    });
  }

  const error = localError ?? (mutation.isError ? uploadErrorText(mutation.error, t) : undefined);
  return (
    <div className="flex flex-col gap-4">
      <form onSubmit={submit} noValidate className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <TextField
          key={inputKey}
          id={`${source}-file`}
          type="file"
          accept=".csv,.txt,text/csv,text/plain"
          label={t(panel ? "admin.imports.panel.file" : "admin.imports.file")}
          error={error}
          className="sm:flex-1 [&_input]:py-2"
          onChange={(event) => {
            setFile(event.target.files?.[0] ?? null);
            setLocalError(undefined);
            mutation.reset();
          }}
        />
        <Button type="submit" disabled={mutation.isPending} className={error ? "sm:mb-7" : undefined}>
          <Upload aria-hidden="true" className="h-4 w-4" />
          {mutation.isPending
            ? t("admin.imports.uploading")
            : t(panel ? "admin.imports.panel.upload" : "admin.imports.upload")}
        </Button>
      </form>
      <div aria-live="polite">
        {mutation.data ? (
          <p
            className="rounded-md bg-success-bg px-3 py-2 text-sm text-success"
            data-testid={`upload-result-${source}`}
          >
            {t("admin.imports.uploaded", { file: mutation.data.fileName })}{" "}
            <Link to={`/admin/imports/${mutation.data.id}`} className="font-semibold underline">
              {t("admin.imports.followRun")}
            </Link>
          </p>
        ) : null}
      </div>
    </div>
  );
}

function uploadErrorText(error: unknown, t: TFunction): string {
  if (error instanceof ApiRequestError) {
    if (error.status === 413) return t("admin.imports.refused.too_large");
    if (error.code === ERROR_CODES.VALIDATION_FAILED) {
      const details = error.details as { reason?: string; columns?: string[] } | undefined;
      if (details?.reason === "missing_columns") {
        return t("admin.imports.refused.missing_columns", { columns: (details.columns ?? []).join(", ") });
      }
      if (details?.reason)
        return t(`admin.imports.refused.${details.reason}`, { defaultValue: t("admin.errors.validation") });
    }
    if (error.code === ERROR_CODES.FORBIDDEN) return t("admin.errors.forbidden");
  }
  return t("auth.genericError");
}

const STATUS_STYLES: Record<ImportRunStatus, { className: string; Icon: typeof Clock; spin?: boolean }> = {
  QUEUED: { className: "bg-bg text-fg-muted", Icon: Clock },
  RUNNING: { className: "bg-warning-bg text-warning-fg", Icon: LoaderCircle, spin: true },
  APPLIED: { className: "bg-success-bg text-success", Icon: CircleCheck },
  FAILED: { className: "bg-danger-bg text-danger", Icon: CircleAlert },
};

/** Icon + text, never colour alone (BRAND.md). */
export function RunStatusBadge({ status }: { status: ImportRunStatus }) {
  const { t } = useTranslation();
  const { className, Icon, spin } = STATUS_STYLES[status];
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap",
        className,
      )}
    >
      <Icon aria-hidden="true" className={clsx("h-3.5 w-3.5", spin && "motion-safe:animate-spin")} />
      {t(`admin.imports.status.${status}`)}
    </span>
  );
}

/** One line saying what a run did (or why it did nothing). */
export function runSummary(run: ImportRun, t: TFunction, language: string): string {
  if (run.status === "FAILED") return t(`admin.imports.failure.${run.failureReason ?? "processing_failed"}`);
  if (!run.counts) return t("admin.imports.inProgress");
  const n = new Intl.NumberFormat(language).format;
  if (isPanelCounts(run.counts)) {
    const p = run.counts.panel;
    return t("admin.imports.panel.summary", {
      count: run.counts.linesRead,
      lines: n(run.counts.linesRead),
      suppliers: n(p.suppliers),
      entries: n(p.entries),
    });
  }
  const r = run.counts.requisitions;
  // `count` picks the plural; the figures shown are formatted for the language.
  return t("admin.imports.summary", {
    count: run.counts.linesRead,
    lines: n(run.counts.linesRead),
    added: n(r.added),
    replaced: n(r.replaced),
    unchanged: n(r.unchanged),
    rejected: n(r.rejected),
  });
}

function RecentRuns() {
  const { t, i18n } = useTranslation();
  const runs = useImportRuns();
  const dateFormat = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" });
  return (
    <Card title={t("admin.imports.recent")}>
      {runs.isError ? (
        <p role="alert" className="rounded-sm bg-danger-bg px-3 py-2 text-sm text-danger">
          {t("auth.genericError")}
        </p>
      ) : null}
      {runs.data && runs.data.length === 0 ? (
        <p className="text-sm text-fg-muted">{t("admin.imports.noRuns")}</p>
      ) : null}
      {runs.data && runs.data.length > 0 ? (
        <table className="w-full text-left text-sm">
          <caption className="sr-only">{t("admin.imports.recent")}</caption>
          <thead className="border-b border-border text-fg-muted">
            <tr>
              <th scope="col" className="py-2 pr-4 font-semibold">
                {t("admin.imports.file")}
              </th>
              <th scope="col" className="py-2 pr-4 font-semibold">
                {t("admin.imports.result")}
              </th>
              <th scope="col" className="hidden py-2 font-semibold md:table-cell">
                {t("admin.imports.by")}
              </th>
            </tr>
          </thead>
          <tbody>
            {runs.data.map((run) => (
              <tr key={run.id} className="border-b border-border last:border-0">
                <td className="py-3 pr-4 align-top">
                  <Link
                    to={`/admin/imports/${run.id}`}
                    className="font-semibold break-all text-primary underline-offset-2 hover:underline"
                  >
                    {run.fileName}
                  </Link>
                  <div className="text-fg-muted">
                    {t(`admin.imports.sources.${run.source}`)} · {dateFormat.format(new Date(run.createdAt))}
                  </div>
                </td>
                <td className="py-3 pr-4 align-top">
                  <RunStatusBadge status={run.status} />
                  <div className="mt-1 text-fg">{runSummary(run, t, i18n.language)}</div>
                </td>
                <td className="hidden py-3 align-top md:table-cell">{run.importedBy ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </Card>
  );
}
